import React from 'react';
import { cn } from '@/lib/utils';

interface NumberInputProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  unit?: string;
  prefix?: string;
  error?: string;
  min?: number;
  max?: number;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  align?: 'left' | 'right';
  'aria-label'?: string;
}

export default function NumberInput({
  label,
  value,
  onChange,
  placeholder = '0',
  unit,
  prefix,
  error,
  min = 0,
  max,
  disabled = false,
  className,
  inputClassName,
  align = 'right',
  'aria-label': ariaLabel,
}: NumberInputProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let newValue = e.target.value;
    if (newValue === '' || /^\d*\.?\d*$/.test(newValue)) {
      if (newValue !== '' && min > 0) {
        const numVal = parseFloat(newValue);
        if (!isNaN(numVal) && numVal <= 0) {
          return;
        }
      }
      if (max !== undefined && newValue !== '') {
        const numVal = parseFloat(newValue);
        if (!isNaN(numVal) && numVal > max) {
          newValue = String(max);
        }
      }
      onChange(newValue);
    }
  };

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <span className="mb-1.5 block text-[13px] font-medium text-foreground">
          {label}
        </span>
      )}
      <div
        className={cn(
          'flex items-center overflow-hidden rounded-lg border bg-card transition-shadow focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary',
          error ? 'border-red-400 focus-within:border-red-500 focus-within:ring-red-100' : 'border-border',
          disabled && 'bg-muted/40 opacity-60 cursor-not-allowed'
        )}
      >
        {prefix && (
          <span className="shrink-0 border-r border-border bg-gray-50 px-2.5 py-2 text-[13px] text-muted-foreground select-none">
            {prefix}
          </span>
        )}
        <input
          type="text"
          inputMode="numeric"
          aria-label={ariaLabel || label}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'w-full min-w-0 bg-transparent px-2.5 py-2 text-[13px] text-foreground outline-none placeholder:text-muted-foreground/60',
            align === 'right' ? 'text-right' : 'text-left',
            inputClassName
          )}
        />
        {unit && (
          <span className="shrink-0 border-l border-border bg-gray-50 px-2.5 py-2 text-[13px] text-muted-foreground select-none">
            {unit}
          </span>
        )}
      </div>
      {error && (
        <p className="text-[11px] text-red-500 mt-1">{error}</p>
      )}
    </div>
  );
}

